"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Flex = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const React = __importStar(require("react"));
const classnames_1 = __importDefault(require("classnames"));
require("@itwin/itwinui-css/css/utils.css");
const sizeTokens = [
    '3xs',
    '2xs',
    'xs',
    's',
    'm',
    'l',
    'xl',
    '2xl',
    '3xl',
];
const getValueForToken = (token) => {
    if (sizeTokens.includes(token)) {
        return `var(--iui-size-${token})`;
    }
    return token;
};
// ----------------------------------------------------------------------------
// Main Flex component
const FlexComponent = React.forwardRef((props, ref) => {
    const { as: Element = 'div', display, flexDirection, justifyContent, alignItems, gap, flexWrap, className, style, ...rest } = props;
    return (React.createElement(Element, { className: (0, classnames_1.default)('iui-flex', className), style: {
            '--iui-flex-display': display,
            '--iui-flex-direction': flexDirection,
            '--iui-flex-justify': justifyContent,
            '--iui-flex-align': alignItems,
            '--iui-flex-gap': getValueForToken(gap),
            '--iui-flex-wrap': flexWrap,
            ...style,
        }, ref: ref, ...rest }));
});
// ----------------------------------------------------------------------------
// Flex.Spacer component
const FlexSpacer = React.forwardRef((props, ref) => {
    const { as: Element = 'div', flex, className, style, ...rest } = props;
    return (React.createElement(Element, { className: (0, classnames_1.default)('iui-flex-spacer', className), style: { '--iui-flex-spacer-flex': flex, ...style }, ref: ref, ...rest }));
});
// ----------------------------------------------------------------------------
// Flex.Item subcomponent
const FlexItem = React.forwardRef((props, ref) => {
    const { as: Element = 'div', gapBefore, gapAfter, flex, alignSelf, className, style, ...rest } = props;
    const _style = {
        '--iui-flex-item-flex': flex,
        '--iui-flex-item-align': alignSelf,
        '--iui-flex-item-gap-before': getValueForToken(gapBefore),
        '--iui-flex-item-gap-after': getValueForToken(gapAfter),
        ...(gapBefore !== undefined && {
            '--iui-flex-item-gap-before-toggle': 'var(--iui-on)',
        }),
        ...(gapAfter !== undefined && {
            '--iui-flex-item-gap-after-toggle': 'var(--iui-on)',
        }),
        ...style,
    };
    return (React.createElement(Element, { className: (0, classnames_1.default)('iui-flex-item', className), ref: ref, style: _style, ...rest }));
});
// ----------------------------------------------------------------------------
// Exported compound component
/**
 * A utility component that makes it easier to work with CSS flexbox
 * and iTwinUI design tokens. Supports all flex properties.
 * Can be used with or without `Flex.Item` and `Flex.Spacer` subcomponents.
 *
 * @example
 * <Flex>
 *   <Icon>...</Icon>
 *   <Text>...</Text>
 *   <Flex.Spacer />
 *   <IconButton>...</IconButton>
 * </Flex>
 *
 * @example
 * <Flex gap='m' flexWrap='wrap'>
 *   <Flex.Item>...</Flex.Item>
 *   <Flex.Item>...</Flex.Item>
 *   ...
 * </Flex>
 *
 * @example
 * <Flex gap='l'>
 *   <Flex.Item>...</Flex.Item>
 *   <Flex.Item>...</Flex.Item>
 *   <Flex.Item gapBefore='s'>...</Flex.Item>
 *   <Flex.Item>...</Flex.Item>
 * </Flex>
 */
exports.Flex = Object.assign(FlexComponent, {
    /**
     * Subcomponent that allows customizing flex items through the
     * `flex`, `alignSelf` and `gapBefore`/`gapAfter` props.
     *
     * The `gapBefore`/`gapAfter` props can used to override the gap at an
     * individual level, for when you need a different gap than the one
     * set in the parent `Flex`.
     *
     * @example
     * <Flex gap='l'>
     *   <Flex.Item>...</Flex.Item>
     *   <Flex.Item>...</Flex.Item>
     *   <Flex.Item gapBefore='s'>...</Flex.Item>
     *   <Flex.Item>...</Flex.Item>
     * </Flex>
     */
    Item: FlexItem,
    /**
     * Subcomponent that fills up the available space using a really large `flex` value.
     * Useful when you want to push certain items to one edge.
     *
     * @example
     * <Flex>
     *   <div>this stays on the left</div>
     *   <Flex.Spacer /> // this fills up the available empty space
     *   <div>this gets pushed all the way to the end</div>
     * </Flex>
     */
    Spacer: FlexSpacer,
});
exports.default = exports.Flex;
